import React, { Fragment, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {  Grid, Fab, Dialog, TextField, CircularProgress,
	  DialogContent, DialogTitle, IconButton, Avatar,
	  AppBar, Toolbar, Button, Input, FormGroup, FormControlLabel, Switch,
	  List, ListItem, ListSubheader, Divider } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Add, Delete } from '@material-ui/icons';
import Typography from '@material-ui/core/Typography';
import { DialogAcceptButton, DialogCancelButton, Transition, isEmailValid, isNameValid } from '../Common.js';
import HeaderBar from './HeaderBar.js';
import MainNavigation from './MainNavigation.js';
import firebase from '../Firebase.js';
import { languageOptions, langCodes } from '../i18n'

const useStyles = makeStyles(
  (theme) =>
    createStyles({
      container: {
	overflow: 'auto',
	'-webkit-overflow-scrolling': 'touch',
	marginTop: theme.spacing(2),
	[theme.breakpoints.up('sm')] : {
	  height: `calc(100% - 64px)`
	},
	[theme.breakpoints.down('xs')] : {
	  height: `calc(100% - 56px)`
	},
      },
      searchBox: {
	backgroundColor: theme.palette.background.paper,
      },
      addTrainerBtn: {
	position: 'absolute',
	bottom: theme.spacing(2),
	right: theme.spacing(2),
	color: theme.palette.primary.main,
	background: theme.palette.primary.green,
	'&:hover': {
	  background: theme.palette.primary.green,
	},
      },
      removeDialogContent: {
	padding: 0,
      },
      dialogBnt: {
	padding: theme.spacing(1),
      },
      input: {
	margin: theme.spacing(1),
      },
      hasRegistered: {
	color: theme.palette.primary.green,
      },
      hasNotRegistered: {
	color: theme.palette.primary.red,
      },
      avatar: {
	width: 70,
	height: 70,
      },
    }),
);

const AddNewTrainerDialog = ({ addTrainerDialog, setAddTrainerDialog, trainers, setTrainers }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [about, setAbout] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const createTrainerInFb = async () => {
    setSaving(true);
    try {
      const data = {name, email, about};
      await firebase.functions()
	.httpsCallable('createTrainer')(data);
      setTrainers([{name, email, about, hasRegistered: false, acceptedByAdmin: true}].concat(trainers));
      setSaving(false);
      cleanAndCloseDialog();
    }
    catch (errors) {
      console.log('errors saving state', errors);
    }
  };

  const areInputsValid = () => {
    return isEmailValid(email) && isNameValid(name);
  };

  const cleanAndCloseDialog = () => {
    setName('');
    setEmail('');
    setAbout('');
    setAddTrainerDialog(false);
  };

  return (
    <Dialog fullScreen
	    open={addTrainerDialog}
	    onClose={cleanAndCloseDialog}
	    TransitionComponent={Transition}>
      <AppBar className={classes.appBar}>
	<Toolbar disableGutters>
	  <Grid container justify='space-between' alignItems='center'>
	    <Grid item xs={3}>
	      <Button color='secondary'
		      disabled={saving}
		      onClick={cleanAndCloseDialog}>
		{ t('cancel') }
	      </Button>
	    </Grid>
	    <Grid item xs={6} container justify='center'>
	      <Typography noWrap align='center' variant='h6'>
		{t('add-trainer')}
	      </Typography>
	    </Grid>
	    <Grid item xs={3} container justify='flex-end'>
	      {
		saving ? (<CircularProgress size={20} color='secondary' />) :
		  <Button color='secondary'
			  disabled={!areInputsValid()}
			  onClick={() => createTrainerInFb()}>
		    { t('save') }
		  </Button>
	      }
	    </Grid>
	  </Grid>
	</Toolbar>
      </AppBar>
      <Grid container direction='column' style={{marginTop: '70px'}}>
	<Grid item container direction='column' style={{padding: '1rem'}}>
	  <Typography gutterBottom variant='caption'>
	    { t('name') + ' *' }
	  </Typography>
	  <Input
	    defaultValue={name}
	    onChange={(e) => { setName(e.target.value.trim()); }}
	    inputProps={{'aria-label': t('name')}}/>
	</Grid>
	<Grid item container direction='column' style={{padding: '1rem'}}>
	  <Typography gutterBottom variant='caption'>
	    { t('email') + ' *'}
	  </Typography>
	  <Input
	    defaultValue={email}
	    onChange={(e) => { setEmail(e.target.value.trim()); }}
	    type='email'
	    inputProps={{'aria-label': t('email') }}/>
	</Grid>
	<Grid item container direction='column' style={{padding: '1rem'}}>
	  <Typography gutterBottom variant='caption'>
	    { t('about-trainer') }
	  </Typography>
	  <Input
	    defaultValue={about}
	    multiline
	    rows={4}
	    onChange={(e) => { setAbout(e.target.value.trim()); }}
	    inputProps={{'aria-label': t('about-trainer') }}/>
	</Grid>
      </Grid>
    </Dialog>
  );
};

const DeleteTrainerDialog = ({deleteTrainer, trainers, setTrainers, open, close}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [deleting, setDeleting] = useState(false);

  const deleteTrainerInFb = async () => {
    setDeleting(true);
    try {
      await firebase.functions()
	.httpsCallable('deleteTrainer')({email: deleteTrainer.email});
      close();
      setTrainers(trainers.filter(tr => tr.email !== deleteTrainer.email));
      setDeleting(false);
    }
    catch (error) {
      close();
      setDeleting(false);
      console.log('error deleting the trainer ', error);
    }
  };

  return (
    <Dialog open={open}
	    onClose={close}
	    fullWidth
	    aria-labelledby='flag-this-exam'>
      <DialogTitle style={{textAlign: 'center'}}
		   disableTypography
		   onClose={() => {}}>
	<Typography gutterBottom variant='h6'>
	  { t('Delete trainer') + ' ' + deleteTrainer.name + '?' }
	</Typography>
	<Typography variant='body2'>
	  { t('are-you-sure-delete') }
	</Typography>
      </DialogTitle>
      <DialogContent dividers className={classes.removeDialogContent}>

	<DialogAcceptButton fullWidth
			    onClick={() => { deleteTrainerInFb(); }}>
	  {
	    deleting ? (<CircularProgress size={20} color='secondary' />) : t('delete')
	  }
	</DialogAcceptButton>
	<Divider/>
	<DialogCancelButton fullWidth
			    disabled={deleting}
			    onClick={close}>
	  { t('cancel') }
	</DialogCancelButton>
      </DialogContent>
    </Dialog>
  );
};

const Toggle = ({trainer}) => {
  const { t } = useTranslation();
  const { email, acceptedByAdmin } = trainer;
  const [checked, setChecked] = useState(acceptedByAdmin);
  const [lockSwitch, setLockSwitch] = useState(false);
  const changeTrainerAsAcceptedInFb = async (isAccepted) => {
    setChecked(isAccepted);
    setLockSwitch(true);
    try {
      await firebase.functions()
	.httpsCallable('acceptAsTrainer')({ email, isAccepted });
      setLockSwitch(false);
    }
    catch (errors) {
      console.log('errors saving state', errors);
    }
  };

  return (
    <FormGroup row style={{alignItems: 'center'}}>
      <FormControlLabel
	control={<Switch checked={checked}
			 disabled={lockSwitch}
			 onChange={(e) => changeTrainerAsAcceptedInFb(e.target.checked)}/>}
	label={t('accept')}/>
      { lockSwitch ? (<CircularProgress size={20} color='secondary'/>) : '' }
    </FormGroup>
  );
};

const ToggleLangague = ({ trainer, lang, updateTrainerLangauges }) => {  
  var languages = trainer.languages ? trainer.languages : langCodes
  let en_usIndex = languages.indexOf('en-US')
	  if (en_usIndex !== -1) {
		languages[en_usIndex] = 'en'
	  }
  let langCode = lang.key === "en-US" ? "en" : lang.key
  const [checked, setChecked] = useState(languages.includes(langCode))  
  return (
    <FormControlLabel
      key={lang.key}
      control={
        <Switch
          checked={checked}
          onChange={(e) => {
            updateTrainerLangauges(trainer, e.target.checked, langCode)
            setChecked(e.target.checked);
          }}
        />
      }
      label={lang.label}
    />
  );
};

export default () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [addTrainerDialog, setAddTrainerDialog] = useState(false);
  const [deleteTrainer, setDeleteTrainer] = useState({});
  const [trainers, setTrainers] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [openDelete, setOpenDelete] = useState(false);

  const closeDelete = _ => setOpenDelete(false);

  const filterTrainers = () => {
    const txt = searchText.toLowerCase();
    return trainers.filter((item) => {
      return (item.name || '').toLowerCase().includes(txt)
	|| item.email.toLowerCase().includes(txt)
	|| item.about.toLowerCase().includes(txt);
    });
  };

  const updateTrainerLangauges = async (trainer, isLanguageSelected, langCode) => {	  	  
	  var languages = trainer.languages ? trainer.languages : langCodes
	  if (isLanguageSelected) {
		if (languages.filter((item) => item === langCode)) {
			languages.push(langCode)
		}      
    } else {
      languages = languages.filter((item) => item !== langCode)
	}
	let en_usIndex = languages.indexOf('en-US')
	  if (en_usIndex !== -1) {
		languages[en_usIndex] = 'en'
	  }
	const trainerIndex = trainers.findIndex(item => item.email === trainer.email )		
	if (trainers[trainerIndex].languages === undefined) {
		trainers[trainerIndex].languages = {}
	}
	trainers[trainerIndex].languages = languages
    await firebase
      .firestore()
      .collection("trainers")
      .doc(trainer.email)
      .update({ languages: languages })	  
  }

  useEffect(() => {
    const getTrainers = async () => {
      try {
	const rez = await firebase.firestore().collection('trainers')
	      .where('hasRoleAdmin', '==', false)
	      .orderBy('createdAt', 'desc').get();

	const trainersProfilesProms = rez.docs.map((doc) => {
	  return firebase.firestore().collection('trainers').doc(doc.id).collection('profile').doc('details').get();
	});

	const trainersProfiles = await Promise.all(trainersProfilesProms);
	const trainers = rez.docs.reduce((acc, doc, i) => {
	  return acc.concat([{ ...doc.data(), ...trainersProfiles[i].data(), email: doc.id}]);
	}, []);

	setLoading(false);
	setTrainers(trainers);
      }
      catch (errors) {
	console.log(errors);
	setLoading(true);
      }
    };
    getTrainers();
  }, []);

  return (
    <div style={{height: '100%'}}>
      <HeaderBar LComp={<MainNavigation />}
		 MComp={<Typography variant='h6' noWrap align='center'>{t('trainers')}</Typography>}
		 RComp={<div></div>}/>
      { loading
	?  <Grid container justify='center' alignItems='center' className={classes.container}>
	     <CircularProgress color='secondary'/>
	   </Grid>
	: <Fragment>
	    { trainers.length === 0
	      ? <Grid className={classes.container} container justify='center' alignItems='center'>
		  <Typography align='center' variant='h2'>
		    {t('no-trainers-yet')}
		  </Typography>
		</Grid>
	      : <List disablePadding className={classes.container}>
		  <ListSubheader className={classes.searchBox}>
		    <Grid container>
		      <TextField
			fullWidth
			type='search'
			value={searchText}
			placeholder={t('search-trainer')}
			onChange={(e) =>{
			  setSearchText(e.target.value.trim());
			}}
			margin='normal'/>
		    </Grid>
		  </ListSubheader>
		  { filterTrainers().map((trainer) => {
		    return (
          <Fragment key={trainer.email}>
            <ListItem onClick={() => {}}>
              <Grid container alignItems="center">
                <Grid item xs={3}>
                  <Avatar
                    alt="avatar"
                    src={trainer.avatar}
                    className={classes.avatar}
                  />
                </Grid>
                <Grid item container xs={7} direction="column">
                  <Typography gutterBottom variant="h6">
                    {trainer.name}
                  </Typography>
                  <Typography gutterBottom variant="body1">
                    {trainer.email}
                  </Typography>
                  <Typography gutterBottom variant="body2">
                    {trainer.about}
                  </Typography>
                  <Typography gutterBottom variant="subtitle2">
                    <span
                      className={
                        trainer.hasRegistered
                          ? classes.hasRegistered
                          : classes.hasNotRegistered
                      }
                    >
                      {trainer.hasRegistered
                        ? t("trainer-has-registered")
                        : t("trainer-has-not-register-yet")}
                    </span>
                  </Typography>
                  <Toggle trainer={trainer} />
                  <FormGroup row style={{ alignItems: "center" }}>
                    {languageOptions.map((lang) => {
                      return (						  
                        <ToggleLangague key={lang.key} trainer={trainer} lang={lang} updateTrainerLangauges = {updateTrainerLangauges}/>
                      );
                    })}
                  </FormGroup>
                </Grid>
                <Grid item xs={2}>
                  <IconButton
                    aria-label="delete"
                    size="medium"
                    onClick={() => {
                      setOpenDelete(true);
                      setDeleteTrainer(trainer);
                    }}
                  >
                    <Delete fontSize="large" color="error" />
                  </IconButton>
                </Grid>
              </Grid>
            </ListItem>
            <Divider />
          </Fragment>
        );
		  })}
		</List>

	    }
	    <Fab aria-label={t('add-trainer')}
		 onClick={() => { setAddTrainerDialog(true); }}
		 className={classes.addTrainerBtn}>
	      <Add />
	    </Fab>
	    <DeleteTrainerDialog setTrainers={setTrainers}
				 trainers={trainers}
				 open={openDelete}
				 close={closeDelete}
				 deleteTrainer={deleteTrainer}
				 setDeleteTrainer={setDeleteTrainer}/>
	    <AddNewTrainerDialog addTrainerDialog={addTrainerDialog}
				 setTrainers={setTrainers}
				 trainers={trainers}
				 setAddTrainerDialog={setAddTrainerDialog}/>
	  </Fragment>
      }
    </div>
  );

};
